'use client';

import React from 'react';
import Box from '../../../atoms/Box/Box';
import Row from '../../../atoms/Grid/Row/Row';
import Col from '../../../atoms/Grid/Col/Col';
import Iconography from '../../../atoms/Iconography/Iconography';
import Typography from '../../../atoms/Typography/Typography';
import Separator from '../../../atoms/Separator/Separator';
import Button from '../../../atoms/Button/Button';

import styles from './Footer.module.scss';

type FooterProps = {
  isExpanded: boolean;
  isCollapsed: boolean;
  isHovered: boolean;
  theme: 'light' | 'dark';
  userName: string;
  userEmail: string;
  onSignOut?: () => void;
  onProfileClick?: () => void;
  dashboardSection?: React.ReactNode;
  /** Label for sign out button (i18n support) */
  signOutLabel?: string;
};

const Footer: React.FC<FooterProps> = ({
  isExpanded,
  isCollapsed,
  isHovered,
  theme,
  userName,
  userEmail,
  onSignOut,
  onProfileClick,
  dashboardSection,
  signOutLabel = 'Sign Out',
}) => {
  return (
    <Box pb={8} className={styles.userSection}>
      <Separator theme={theme} />
      {dashboardSection && <Box pt={16}>{dashboardSection}</Box>}
      <Box pt={8}>
        <div
          className={`${styles.profileRow}${onProfileClick ? ` ${styles.profileRowClickable}` : ''}`}
          onClick={onProfileClick}
          role={onProfileClick ? 'button' : undefined}
          tabIndex={onProfileClick ? 0 : undefined}
          onKeyDown={onProfileClick ? (e) => e.key === 'Enter' && onProfileClick() : undefined}
        >
          <Row
            gutter={[8, 8]}
            align="middle"
            justify={isCollapsed && !isHovered ? 'center' : 'start'}
            wrap={false}
          >
            <Col>
              <Box className={styles.avatar}>
                <Iconography category="global" name="user" size={24} color="white" />
              </Box>
            </Col>
            {isExpanded && (
              <>
                <Col className={styles.userDetails}>
                  <Typography variant="body" size="medium" color="white" theme={theme}>
                    {userName}
                  </Typography>
                  <Typography variant="body" size="small" color="gray-5" theme={theme}>
                    {userEmail}
                  </Typography>
                </Col>
                {onProfileClick && (
                  <Col>
                    <Iconography category="global" color="white" name="link-external" />
                  </Col>
                )}
              </>
            )}
          </Row>
        </div>
        {isExpanded && onSignOut && (
          <Box pt={4} pl={4} pr={8} className={styles.signoutContainer}>
            <Button
              gaComponent="Footer"
              dataTestId="sign-out-button"
              variant="link"
              size="medium"
              theme={theme}
              label={signOutLabel}
              type="tertiary"
              iconRight={{ category: 'global', name: 'logout' }}
              handleClick={onSignOut}
              noPadding
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

Footer.displayName = 'Footer';

export default Footer;
